import React, { useState, useEffect } from 'react';
import { VisualAssetEditor } from './VisualAssetEditor';
import { VisualAssetData, ViewportData } from './types';
import { useAsset, assetHelpers, Asset, AssetUpdateInput } from '@/hooks/useAsset';

interface VisualAssetEditorByIdWrapperProps {
  assetId: string;
  onSave: (data: VisualAssetData) => void;
  onCancel?: () => void;
  className?: string;
  showSaveControls?: boolean;
  initialAssetData?: Asset; // Optional initial data to avoid re-fetching
  initialViewportState?: any; // Optional initial viewport state from news modal
  onStateChange?: (state: any) => void; // Callback for live state changes
  enableCollectionChange?: boolean; // Enable collection dropdown in save bar
}

// Simplified conversion from unified Asset to VisualAssetData for editing
const convertAssetToVisualAssetData = (asset: Asset, initialViewportState?: any): VisualAssetData => {
  const primaryDisplayUrl = assetHelpers.getPrimaryDisplayUrl(asset);
  const secondaryDisplayUrl = assetHelpers.getSecondaryDisplayUrl(asset);

  // Determine chronological order for before/after display
  const primaryDate = asset.version ? parseInt(asset.version) : 0;
  const secondaryDate = asset.secondary_version ? parseInt(asset.secondary_version) : 0;

  // If primary is newer than secondary, we need to swap for chronological display
  const shouldSwapForChronology = primaryDate > secondaryDate && secondaryDisplayUrl;

  const beforeImageUrl = shouldSwapForChronology ? secondaryDisplayUrl : primaryDisplayUrl;
  const afterImageUrl = shouldSwapForChronology ? primaryDisplayUrl : secondaryDisplayUrl;
  const beforeVersion = shouldSwapForChronology ? asset.secondary_version : asset.version;
  const afterVersion = shouldSwapForChronology ? asset.version : asset.secondary_version;

  // Create image arrays that match the display order (before/after)
  const beforeImages = shouldSwapForChronology && asset.secondary_original_url ? [{
    version: asset.secondary_version || asset.version || '20250101',
    url: asset.secondary_original_url
  }] : asset.primary_original_url ? [{
    version: asset.version || '20250101',
    url: asset.primary_original_url
  }] : [];

  const afterImages = shouldSwapForChronology && asset.primary_original_url ? [{
    version: asset.version || '20250101',
    url: asset.primary_original_url
  }] : asset.secondary_original_url ? [{
    version: asset.secondary_version || asset.version || '20250101',
    url: asset.secondary_original_url
  }] : [];

  return {
    aspectRatio: asset.crop_aspect_ratio || '1:1', // Use saved aspect ratio or default to 1:1
    viewMode: assetHelpers.getValidViewMode(asset, asset.comparison_mode),
    comparison_mode: asset.comparison_mode || 'single',
    header: asset.header || '',
    subheader: asset.subheader || '',
    companyName: asset.slug || '',
    companyLogo: '',
    primaryImage: beforeImageUrl,  // Show chronologically older image as primary
    secondaryImage: afterImageUrl, // Show chronologically newer image as secondary
    primaryImages: beforeImages,   // Array that matches what's shown as primary
    secondaryImages: afterImages,  // Array that matches what's shown as secondary
    selectedPrimaryVersion: beforeVersion || beforeImages[0]?.version,
    selectedSecondaryVersion: afterVersion || afterImages[0]?.version,
    customData: {
      // Include existing URLs for reference
      primary_cropped_url: asset.primary_cropped_url,
      secondary_cropped_url: asset.secondary_cropped_url,
    },
    initialState: {
      // When swapping for chronology, also swap the viewport states to match
      primary: shouldSwapForChronology ?
        (initialViewportState?.secondary || asset.secondary_viewport_state || undefined) :
        (initialViewportState?.primary || asset.primary_viewport_state || undefined),
      secondary: shouldSwapForChronology ?
        (initialViewportState?.primary || asset.primary_viewport_state || undefined) :
        (initialViewportState?.secondary || asset.secondary_viewport_state || undefined)
    }
  };
};

export const VisualAssetEditorByIdWrapper: React.FC<VisualAssetEditorByIdWrapperProps> = ({
  assetId,
  onSave,
  onCancel,
  className,
  showSaveControls = true,
  initialAssetData,
  initialViewportState,
  onStateChange,
  enableCollectionChange = true
}) => {
  // Use unified asset hook - single source of truth
  const { asset, isLoading, isError, error, updateAsset: updateAssetMutation } = useAsset(assetId);

  // Use provided initial data or fetched data
  const assetData = initialAssetData || asset;

  // Live state tracking for debug - what will be saved to DB
  const [currentDbSaveData, setCurrentDbSaveData] = useState<AssetUpdateInput>({});
  const [lastAction, setLastAction] = useState<string>('initial_load');
  const [actionTimestamp, setActionTimestamp] = useState<string>(new Date().toISOString());

  // Track the current live viewport state from editor
  const [currentLiveState, setCurrentLiveState] = useState<any>(null);

  // Track the current view mode for debug panel updates (initialize with default)
  const [currentViewMode, setCurrentViewMode] = useState<string>('single');

  // Track selected collection ID for save operation
  const [selectedCollectionId, setSelectedCollectionId] = useState<string | undefined>(undefined);

  // Debug panel is commented out - state variables removed for production

  // Initialize currentViewMode with actual data once available - must be at top level
  useEffect(() => {
    if (assetData) {
      const visualAssetData = convertAssetToVisualAssetData(assetData, initialViewportState);
      if (visualAssetData.viewMode && visualAssetData.viewMode !== currentViewMode) {
        setCurrentViewMode(visualAssetData.viewMode);
      }
    }
  }, [assetData, currentViewMode]);

  // Simple function to prepare DB save data - just use what we have in current state
  const prepareDbSaveData = (state: any): AssetUpdateInput => {
    // Get current asset data safely
    const currentAssetData = initialAssetData || asset;
    if (!currentAssetData) {
      return {};
    }

    const visualAssetData = convertAssetToVisualAssetData(currentAssetData);

    // Detect if chronology was swapped (same logic as in convertAssetToVisualAssetData)
    const primaryDate = currentAssetData.version ? parseInt(currentAssetData.version) : 0;
    const secondaryDate = currentAssetData.secondary_version ? parseInt(currentAssetData.secondary_version) : 0;
    const isChronologySwapped = primaryDate > secondaryDate && currentAssetData.secondary_original_url;

    // Ensure viewport states are in the correct database format (pan_x/pan_y)
    const normalizeViewportState = (viewportState: any) => {
      if (!viewportState) return null;

      return {
        zoom: viewportState.zoom || 1,
        pan_x: viewportState.pan_x || 0,
        pan_y: viewportState.pan_y || 0,
        annotations: Array.isArray(viewportState.annotations) ? viewportState.annotations : []
      };
    };

    // Use view mode from the incoming state if available, otherwise fall back to currentViewMode
    const activeViewMode = state?.viewMode || currentViewMode;

    // Ensure viewport states include ALL data (zoom, pan, annotations, crop URLs, aspect ratio)
    const enrichPrimaryViewportState = (viewportState: any) => {
      if (!viewportState) return null;
      return {
        zoom: viewportState.zoom || 1,
        pan_x: viewportState.pan_x || 0,
        pan_y: viewportState.pan_y || 0,
        annotations: Array.isArray(viewportState.annotations) ? viewportState.annotations : [],
        // Include crop data in viewport state (UNIFIED)
        cropped_url: state?.croppedUrls?.primaryCroppedUrl || viewportState.cropped_url,
        aspect_ratio: state?.aspectRatio || viewportState.aspect_ratio || '1:1',
        original_url: viewportState.original_url
      };
    };

    const enrichSecondaryViewportState = (viewportState: any) => {
      if (!viewportState) return null;
      return {
        zoom: viewportState.zoom || 1,
        pan_x: viewportState.pan_x || 0,
        pan_y: viewportState.pan_y || 0,
        annotations: Array.isArray(viewportState.annotations) ? viewportState.annotations : [],
        // Include crop data in viewport state (UNIFIED)
        cropped_url: state?.croppedUrls?.secondaryCroppedUrl || viewportState.cropped_url,
        aspect_ratio: state?.aspectRatio || viewportState.aspect_ratio || '1:1',
        original_url: viewportState.original_url
      };
    };

    const updateData: AssetUpdateInput = {
      comparison_mode: activeViewMode as any, // Use the most current view mode
      header: state?.header || visualAssetData.header,
      subheader: state?.subheader || visualAssetData.subheader,
      // When chronology is swapped, reverse the viewport state mapping to save to correct DB fields
      primary_viewport_state: isChronologySwapped ?
        enrichSecondaryViewportState(state?.secondary) :  // Save "after" data to primary field
        enrichPrimaryViewportState(state?.primary),       // Save "before" data to primary field
      secondary_viewport_state: isChronologySwapped ?
        enrichPrimaryViewportState(state?.primary) :      // Save "before" data to secondary field
        enrichSecondaryViewportState(state?.secondary),   // Save "after" data to secondary field
      // Also reverse cropped URL mapping when chronology is swapped
      primary_cropped_url: isChronologySwapped ?
        state?.croppedUrls?.secondaryCroppedUrl :          // Save "after" URL to primary field
        state?.croppedUrls?.primaryCroppedUrl,             // Save "before" URL to primary field
      secondary_cropped_url: isChronologySwapped ?
        state?.croppedUrls?.primaryCroppedUrl :            // Save "before" URL to secondary field
        state?.croppedUrls?.secondaryCroppedUrl,           // Save "after" URL to secondary field
      crop_aspect_ratio: state?.aspectRatio
    };

    console.log('💾 Simple prepare DB data:', updateData);
    return updateData;
  };

  // Handle save with unified state management and collection update
  const handleSave = async (data: VisualAssetData, collectionId?: string) => {
    try {
      // Build update data with collection_id if changed
      const updateData = { ...currentDbSaveData };

      // Only update collection_id if it's different from current
      if (enableCollectionChange && collectionId && collectionId !== assetData?.collection_id) {
        updateData.collection_id = collectionId;
        console.log('🔄 Updating collection_id from', assetData?.collection_id, 'to', collectionId);
      }

      setLastAction('SAVING TO DB');
      setActionTimestamp(new Date().toISOString());

      console.log('💾 SAVING WITH COLLECTION UPDATE:', updateData);

      // Update the asset using the unified hook
      updateAssetMutation(updateData);

      // Call the original onSave callback
      onSave(data);
    } catch (error) {
      console.error('Failed to save asset:', error);
      setLastAction('SAVE FAILED');
      setActionTimestamp(new Date().toISOString());
    }
  };

  // Handle live state changes from editor (viewport states and annotations)
  const handleEditorStateChange = (state: any) => {

    // Store the current live state
    setCurrentLiveState(state);

    // Update current view mode if it's included in the state
    if (state.viewMode && state.viewMode !== currentViewMode) {
      console.log('🔄 View mode changed:', currentViewMode, '->', state.viewMode);
      setCurrentViewMode(state.viewMode);
    }

    // Update debug display with what will be saved (use the latest state)
    const dbData = prepareDbSaveData(state);
    setCurrentDbSaveData(dbData);
    setLastAction('live_edit');
    setActionTimestamp(new Date().toISOString());

    // Log the active view mode being used for debugging
    const activeViewMode = state?.viewMode || currentViewMode;
    console.log('🎯 Debug panel using view mode:', activeViewMode);

    // Call external onStateChange callback if provided
    if (onStateChange) {
      onStateChange(state);
    }
  };

  if (isLoading && !initialAssetData) {
    return (
      <div className={`flex items-center justify-center bg-black ${className}`}>
        <div className="text-white">Loading asset for editing...</div>
      </div>
    );
  }

  if (isError || !assetData) {
    return (
      <div className={`flex items-center justify-center bg-black ${className}`}>
        <div className="text-red-500">
          {error?.message || 'Failed to load asset for editing'}
        </div>
      </div>
    );
  }

  const visualAssetData = convertAssetToVisualAssetData(assetData, initialViewportState);

  // Safely convert viewport state from database format to editor format
  const convertViewportState = (dbState: any): ViewportData | undefined => {
    if (!dbState) return undefined;

    // Handle both direct object and JSON string formats
    const state = typeof dbState === 'string' ? JSON.parse(dbState) : dbState;

    // Ensure we have the correct format with pan_x/pan_y (database format)
    return {
      zoom: state.zoom || 1,
      pan_x: state.pan_x || 0,
      pan_y: state.pan_y || 0,
      annotations: Array.isArray(state.annotations) ? state.annotations : []
    };
  };

  // Create merged viewport state from asset data and props
  const mergedViewportState = {
    header: initialViewportState?.header || visualAssetData.header,
    subheader: initialViewportState?.subheader || visualAssetData.subheader,
    primary: initialViewportState?.primary || convertViewportState(assetData.primary_viewport_state),
    secondary: initialViewportState?.secondary || convertViewportState(assetData.secondary_viewport_state)
  };

  return (
    <div className="w-full h-full relative">
      {/* Floating Debug Panel - Live DB Save Data - Dismissable */}
      {/* {showDebugPanel && (
        <div className="absolute top-1/2 right-4 -translate-y-1/2 z-50 bg-gray-900 bg-opacity-95 text-white p-3 rounded-lg shadow-lg max-w-md max-h-80 overflow-y-auto text-xs font-mono">
          <div className="flex justify-between items-center mb-2">
            <h4 className="font-bold text-sm text-yellow-300">
              Live DB Save Data
              <span className="text-green-400 ml-2">({lastAction})</span>
            </h4>
            <button
              onClick={() => setShowDebugPanel(false)}
              className="text-gray-400 hover:text-white transition-colors ml-2"
              title="Hide debug panel"
            >
              ✕
            </button>
          </div>
          <div className="text-xs text-gray-400 mb-2">{actionTimestamp}</div>
          <div className="bg-black bg-opacity-50 p-2 rounded">
            <pre className="text-xs overflow-x-auto whitespace-pre-wrap">{JSON.stringify(currentDbSaveData, null, 2)}</pre>
          </div>
          <h4 className="font-bold mb-2 text-sm text-yellow-300 mt-3">Raw GraphQL Data:</h4>
          <div className="bg-black bg-opacity-50 p-2 rounded">
            <pre className="text-xs overflow-x-auto whitespace-pre-wrap">{JSON.stringify(assetData, null, 2)}</pre>
          </div>
          <h4 className="font-bold mb-2 text-sm text-yellow-300 mt-3">Initial Viewport State:</h4>
          <div className="bg-black bg-opacity-50 p-2 rounded">
            <pre className="text-xs overflow-x-auto whitespace-pre-wrap">{JSON.stringify(initialViewportState, null, 2)}</pre>
          </div>
        </div>
      )} */}

      {/* Debug Panel Toggle Button - Always Visible */}
      {/* {!showDebugPanel && (
        <div className="absolute top-4 right-4 z-50">
          <button
            onClick={() => setShowDebugPanel(true)}
            className="bg-gray-800 bg-opacity-90 text-yellow-300 p-2 rounded-lg shadow-lg text-xs font-mono hover:bg-gray-700 transition-colors"
            title="Show debug panel"
          >
            Debug
          </button>
        </div>
      )} */}

      {/* Visual Asset Editor - Full Layout */}
      <VisualAssetEditor
        initialData={visualAssetData}
        onSave={handleSave}
        onCancel={onCancel}
        showSaveControls={showSaveControls}
        initialViewportState={mergedViewportState}
        onStateChange={handleEditorStateChange}
        className={className}
        enableCollectionChange={enableCollectionChange}
        initialCollectionId={assetData?.collection_id}
        onCollectionChange={setSelectedCollectionId}
      />
    </div>
  );
};